import * as companyBillService from "../../services/companyBill";
import * as customerBillService from "../../services/customerBill";
import * as settings from "../../services/settings";

import { Button, Col, InputNumber, Row, Spin, Table, notification } from "antd";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { ModalDestinationService } from "./modal/ModalDestinationService";
import dayjs from "dayjs";
import { formatNumber } from "../../utils/number";
import permissions from "../../../configs/permission";
import { useAccessToken } from "../../contexts/AccessTokenContext";
import { usePermission } from "../../hooks/usePermission";
import { useTranslation } from "react-i18next";

const initPage = { limit: 5, page: 1, total: 1 };
let promiseAwait;
export const CreateOriginPackageTransportationService = ({
  isCompanyBill,
  companyBillItemId,
  dataCompanyBill,
  onItemsChange,
  translations,
}) => {
  const { t } = useTranslation(translations);
  const { hasPermission, isSuperuser } = usePermission();
  const hasFetchedRef = useRef(false);
  const { accessToken } = useAccessToken();
  const [loading, setLoading] = useState(false);
  const [companyBillItem, setCompanyBillItem] = useState([]);
  const [search, setSearch] = useState(initPage);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [item, setItem] = useState();
  const [editOriginPackageServiceData, setEditOriginPackageServiceData] = useState([]);
  const [isEdit, setIsEdit] = useState(false);
  const [total, setTotal] = useState(0);
  const [sumTotal, setSumTotal] = useState(0);

  const [editRows, setEditRows] = useState({}); // key: id, value: { od, wc, in, dd, rate }

  const [api, contextHolder] = notification.useNotification();
  const openNotificationWithIcon = (type, message, description) => {
    api[type]({
      message: message,
      description: description,
    });
  };

  const isLockedOriginPackageRow = (row) =>
    row?.description === "origin_package_service" && !row?.app_originpackagetransportation?.app_originpackage;

  const showModal = () => {
    setIsModalOpen(true);
  };
  const handleOk = () => {
    setIsModalOpen(false);
  };
  const handleCancel = () => {
    setIsModalOpen(false);
  };

  // helper รวม total ของแต่ละแถว
  const calcRowTotal = (row) => {
    const originPackage = row.app_originpackagetransportation?.app_originpackage ?? {};
    const originService = originPackage.app_originpackageservice ?? [];
    const getServiceCost = (type) => Number(originService.find((s) => s.package_service === type)?.service_cost || 0);
    const od = getServiceCost("OD");
    const wc = getServiceCost("WC");
    const inVal = getServiceCost("IN");
    const ot = getServiceCost("OT");
    const dd = Number(row.app_destinationpackageservice?.service_cost || 0);
    const rate = Number(row?.rate || 0);
    const shippingCost = Number(row.bill_amount || 0); // ใช้จาก field bill_amount

    return od + wc + inVal + ot + dd + shippingCost;
    // return (od + wc + inVal + dd) * rate + shippingCost;
  };

  // helper ดึงค่า service แต่ละประเภทจากแถว
  const getServiceCostFromRow = (row, type) => {
    const ops = row.app_originpackagetransportation?.app_originpackage?.app_originpackageservice ?? [];
    return Number(ops.find((s) => s.package_service === type)?.service_cost || 0);
  };

  // ---- helper: สร้าง summary เพื่อส่งให้ parent ----
  const buildSummaryFromRows = (rows) => {
    const items = rows.map((row) => {
      const total_od = getServiceCostFromRow(row, "OD");
      const total_wc = getServiceCostFromRow(row, "WC");
      const total_in = getServiceCostFromRow(row, "IN");
      const total_ot = getServiceCostFromRow(row, "OT");
      const dd = Number(row.app_destinationpackageservice?.service_cost || 0);
      return { ...row, total_od, total_wc, total_in, total_ot, package_service_dd: dd };
    });

    const packageTotal = items.reduce((sum, r) => sum + Number(r.bill_amount || 0), 0); // รวม shipping
    const sumTotal = items.reduce(
      (sum, r) =>
        sum +
        Number(r.total_od || 0) +
        Number(r.total_wc || 0) +
        Number(r.total_in || 0) +
        Number(r.total_ot || 0) +
        Number(r.package_service_dd || 0),
      0
    );
    const totalPrice = sumTotal + packageTotal;

    return { items, sumTotal, packageTotal, totalPrice };
  };

  // helper รวม total ทั้งหมด
  const calcTotalAll = () => {
    return companyBillItem.reduce((sum, row) => sum + calcRowTotal(row), 0);
  };

  // Event แก้ไขแต่ละช่อง
  const handleInputChange = (value, id, field) => {
    setCompanyBillItem((prev) =>
      prev.map((item) => {
        if (item.id !== id) return item;

        if (field === "rate") {
          return {
            ...item,
            rate: value,
          };
        }

        if (field === "dd") {
          return {
            ...item,
            app_destinationpackageservice: {
              ...item.app_destinationpackageservice,
              service_cost: value,
            },
          };
        }

        // OD / WC / IN / OT (origin package service)
        const originPackage = item.app_originpackagetransportation?.app_originpackage ?? {};
        const originServices = originPackage.app_originpackageservice ?? [];

        const serviceIndex = originServices.findIndex((s) => s.package_service === field.toUpperCase());

        let updatedServices;
        if (serviceIndex >= 0) {
          // เคส: มีอยู่แล้ว → อัปเดต
          updatedServices = originServices.map((s, i) => (i === serviceIndex ? { ...s, service_cost: value } : s));
        } else {
          // เคส: ไม่มีเลย → เพิ่มใหม่
          updatedServices = [
            ...originServices,
            {
              package_service: field.toUpperCase(),
              service_cost: value,
            },
          ];
        }

        return {
          ...item,
          app_originpackagetransportation: {
            ...item.app_originpackagetransportation,
            app_originpackage: {
              ...originPackage,
              app_originpackageservice: updatedServices,
            },
          },
        };
      })
    );
  };

  /** Coerce Prisma Decimal / string from API to finite number for bill payload. */
  const toServiceCostNumber = (raw) => {
    if (raw == null || raw === "") return 0;
    if (typeof raw === "object" && raw !== null && typeof raw.toString === "function") {
      const n = Number(raw.toString());
      return Number.isFinite(n) ? n : 0;
    }
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  };

  /**
   * Before save: if bill line has origin package but no OT row in nested state,
   * append OT @ 0 so payload always sends package_service_ot (backend create/update).
   */
  const ensureOtOriginServiceOnRow = (row) => {
    const pkg = row?.app_originpackagetransportation?.app_originpackage;
    const originPkId = Number(pkg?.id ?? row?.app_originpackagetransportation?.origin_package_id ?? 0);
    if (!pkg || !Number.isFinite(originPkId) || originPkId <= 0) return row;

    const services = [...(pkg.app_originpackageservice ?? [])];
    if (services.some((s) => s.package_service === "OT")) return row;

    return {
      ...row,
      app_originpackagetransportation: {
        ...row.app_originpackagetransportation,
        app_originpackage: {
          ...pkg,
          app_originpackageservice: [...services, { package_service: "OT", service_cost: 0 }],
        },
      },
    };
  };

  // ปุ่ม save
  const handleSave = async () => {
    const rowsForPayload = companyBillItem.map(ensureOtOriginServiceOnRow);

    const payload = rowsForPayload.map((row) => {
      const originPackage = row.app_originpackagetransportation?.app_originpackage || {};
      const destinationPackage =
        row.app_originpackagetransportation?.app_originpackage?.app_destinationpackage[0] || null;
      const originService = originPackage.app_originpackageservice || [];
      const getServiceCost = (type) => {
        const found = originService.find((s) => s.package_service === type);
        return toServiceCostNumber(found?.service_cost);
      };

      const packageServiceOD = getServiceCost("OD");
      const packageServiceWC = getServiceCost("WC");
      const packageServiceIN = getServiceCost("IN");
      const packageServiceOT = getServiceCost("OT");
      const packageServiceDD = toServiceCostNumber(row.app_destinationpackageservice?.service_cost);
      const rate = Number(row.rate || 0);
      const calShippingCost = row.bill_amount;
      const totalBill =
        packageServiceOD + packageServiceWC + packageServiceIN + packageServiceOT + packageServiceDD + calShippingCost;
      const totalBillNumber = parseFloat(totalBill);
      const totalBillFormatted = Number.isFinite(totalBillNumber) ? totalBillNumber.toFixed(3) : "0.000";
      const totalWeight = Number(originPackage.total_weight || 0);
      const totalVolume = Number(originPackage.total_volume || 0);
      return {
        id: row.id,
        bill_amount: calShippingCost,
        package_service_od: packageServiceOD,
        package_service_wc: packageServiceWC,
        package_service_in: packageServiceIN,
        package_service_ot: packageServiceOT,
        package_service_dd: packageServiceDD,
        rate: rate,
        origin_package_id: originPackage?.id ?? row?.app_originpackagetransportation?.origin_package_id ?? null,
        origin_package_transportation_id: row?.origin_package_transportation_id,
        destination_package_service_id: row?.destination_package_service_id,
        destination_package_id: destinationPackage?.id,
        total_bill: totalBillFormatted,
        density: row.density,
        total_weight: totalWeight,
        total_volume: totalVolume,
      };
    });
    try {
      setLoading(true);

      // 1) ยิง API ตามเดิม
      let response;
      if (isCompanyBill) {
        response = await companyBillService.updateCompanyBillItem(accessToken, payload);
      } else {
        response = await customerBillService.updateCustomerBillItem(accessToken, payload);
      }

      // 2) อัปเดต state ให้สะท้อนค่าที่เพิ่งบันทึก (bill_amount/rate/DD/OT) ทันที (เพื่อ UI อัปเดตทันที)
      const updatedRows = rowsForPayload.map((row) => {
        const p = payload.find((x) => x.id === row.id);
        if (!p) return row;

        const opt = row.app_originpackagetransportation;
        const originPackage = opt?.app_originpackage ?? {};
        const originServices = [...(originPackage.app_originpackageservice ?? [])];
        const otIdx = originServices.findIndex((s) => s.package_service === "OT");
        const nextOtServices =
          otIdx >= 0
            ? originServices.map((s, i) => (i === otIdx ? { ...s, service_cost: p.package_service_ot } : s))
            : [...originServices, { package_service: "OT", service_cost: p.package_service_ot }];

        return {
          ...row,
          bill_amount: p.bill_amount,
          rate: p.rate,
          app_destinationpackageservice: {
            ...(row.app_destinationpackageservice || {}),
            service_cost: p.package_service_dd,
          },
          ...(opt
            ? {
                app_originpackagetransportation: {
                  ...opt,
                  app_originpackage: {
                    ...originPackage,
                    app_originpackageservice: nextOtServices,
                  },
                },
              }
            : {}),
        };
      });
      setCompanyBillItem(updatedRows);

      // 3) คำนวณสรุป & เรียก onItemsChange → CompanyBiller เห็น OD/WC/IN/Total ใหม่ทันที
      const summary = buildSummaryFromRows(updatedRows);
      onItemsChange?.(summary);

      // 4) แจ้งผล
      if (response !== null && response !== undefined) {
        openNotificationWithIcon(settings.NotificationType.SUCCESS, <>{"Success."}</>, <>{"Update bill success"}</>);
      }

      // 5) (คงพฤติกรรมเดิม) refetch เพื่อ sync กับฐานข้อมูลภายหลัง
      if (isCompanyBill) {
        const param = { company_bill_no: dataCompanyBill.id, limit: 10, page: 1 };
        fetchCompanyBillItem(param);
      } else {
        const param = { customer_bill_no: dataCompanyBill.id, limit: 10, page: 1 };
        fetchCustomerBillItem(param);
      }
    } catch (err) {
      console.error("[CreateOriginPackageTransportationService] handleSave: ", err);
      setLoading(false);
      const { message } = settings.getErrorMessage(err);
      openNotificationWithIcon(settings.NotificationType.ERROR, <>{"ERROR."}</>, <>{`${message}`}</>);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: t("Origin_Package_Service_Table.Table_Origin_Service", { ns: translations }),
      width: 150,
      // width: 90,
      dataIndex: "origin_service",
      key: "origin_service",
      render: (_, record) => {
        let originpackage = record?.app_originpackagetransportation?.app_originpackage;
        let origintransportation = record?.app_originpackagetransportation?.app_origintransportation;
        return (
          <p>
            {originpackage && origintransportation
              ? originpackage.reference + " / " + origintransportation.container
              : "-"}
          </p>
        );
      },
    },
    {
      title: t("Origin_Package_Service_Table.Table_Package_Type", { ns: translations }),
      width: 150,
      // width: 80,
      dataIndex: "package_type",
      key: "package_type",
      render: (_, record) => {
        const originPackage = record?.app_originpackagetransportation?.app_originpackage;
        return <p>{originPackage?.package_type ? originPackage.package_type : "-"}</p>; // Display the actual description
      },
    },
    {
      title: t("Origin_Package_Service_Table.Table_OD_CNY", { ns: translations }),
      width: 150,
      // width: 100,
      dataIndex: "note",
      key: "note",
      render: (_, record) => {
        const originPackage = record.app_originpackagetransportation?.app_originpackage ?? {};
        const originPackageService = originPackage.app_originpackageservice ?? [];
        const odPackage = originPackageService.find((item) => item.package_service === "OD");
        return <>{odPackage?.note || "-"}</>;
      },
    },
    {
      title: t("Origin_Package_Service_Table.Table_OD", { ns: translations }),
      width: 150,
      // width: 40,
      dataIndex: "package_service_od",
      key: "package_service_od",
      render: (_, record) => {
        const originPackage = record.app_originpackagetransportation?.app_originpackage ?? {};
        const originPackageService = originPackage.app_originpackageservice ?? [];
        const odPackage = originPackageService.find((item) => item.package_service === "OD");
        return (
          <InputNumber
            // style={{ width: 50 }}
            disabled={isLockedOriginPackageRow(record)}
            min={0}
            value={Number(odPackage?.service_cost || 0)}
            onChange={(value) => handleInputChange(value, record.id, "od")}
          />
        );
      },
    },
    {
      title: t("Origin_Package_Service_Table.Table_WC_CNY", { ns: translations }),
      width: 150,
      // width: 100,
      dataIndex: "note",
      key: "note",
      render: (_, record) => {
        const originPackage = record.app_originpackagetransportation?.app_originpackage ?? {};
        const originPackageService = originPackage.app_originpackageservice ?? [];
        const odPackage = originPackageService.find((item) => item.package_service === "WC");
        return <>{odPackage?.note || "-"}</>;
      },
    },
    {
      title: t("Origin_Package_Service_Table.Table_WC", { ns: translations }),
      width: 40,
      dataIndex: "package_service_wc",
      key: "package_service_wc",
      render: (_, record) => {
        const originPackage = record.app_originpackagetransportation?.app_originpackage ?? {};
        const originPackageService = originPackage.app_originpackageservice ?? [];
        const wcPackage = originPackageService.find((item) => item.package_service === "WC");
        return (
          <InputNumber
            // style={{ width: 50 }}
            disabled={isLockedOriginPackageRow(record)}
            min={0}
            value={Number(wcPackage?.service_cost || 0)}
            onChange={(value) => handleInputChange(value, record.id, "wc")}
          />
        );
      },
    },
    {
      title: t("Origin_Package_Service_Table.Table_IN_CNY", { ns: translations }),
      width: 150,
      // width: 90,
      dataIndex: "note",
      key: "note",
      render: (_, record) => {
        const originPackage = record.app_originpackagetransportation?.app_originpackage ?? {};
        const originPackageService = originPackage.app_originpackageservice ?? [];
        const odPackage = originPackageService.find((item) => item.package_service === "IN");
        return <>{odPackage?.note || "-"}</>;
      },
    },
    {
      title: t("Origin_Package_Service_Table.Table_IN", { ns: translations }),
      width: 150,
      // width: 40,
      dataIndex: "package_service_in",
      key: "package_service_in",
      render: (_, record) => {
        const originPackage = record.app_originpackagetransportation?.app_originpackage ?? {};
        const originPackageService = originPackage.app_originpackageservice ?? [];
        const inPackage = originPackageService.find((item) => item.package_service === "IN");
        return (
          <InputNumber
            // style={{ width: 50 }}
            disabled={isLockedOriginPackageRow(record)}
            min={0}
            value={Number(inPackage?.service_cost || 0)}
            onChange={(value) => handleInputChange(value, record.id, "in")}
          />
        );
      },
    },
    {
      title: t("Origin_Package_Service_Table.Table_DD", { ns: translations }),
      width: 150,
      // width: 40,
      dataIndex: "package_service_dd",
      key: "package_service_dd",
      render: (_, record) => {
        const destinationPackageService = record.app_destinationpackageservice;
        return (
          <InputNumber
            // style={{ width: 50 }}
            disabled={
              isLockedOriginPackageRow(record) ||
              !(record.app_originpackagetransportation?.app_originpackage?.app_destinationpackage?.length > 0)
            }
            min={0}
            value={Number(destinationPackageService?.service_cost || 0)}
            onChange={(value) => handleInputChange(value, record.id, "dd")}
          />
        );
      },
    },
    {
      title: t("Origin_Package_Service_Table.Table_Volume", { ns: translations }),
      width: 150,
      // width: 80,
      dataIndex: "total_volume",
      key: "total_volume",
      render: (_, record) => {
        const value = record.app_originpackagetransportation?.app_originpackage?.total_volume || 0;
        return <div className="text-right">{formatNumber(value, 6, "round")}</div>;
        // return <div className="text-right"> {formatNumber(Math.ceil(value * 1000) / 1000, 2)}</div>;
      },
    },
    {
      title: t("Origin_Package_Service_Table.Table_Weight", { ns: translations }),
      width: 150,
      // width: 80,
      dataIndex: "total_weight",
      key: "total_weight",
      render: (_, record) => {
        const value = record.app_originpackagetransportation?.app_originpackage?.total_weight || 0;
        return <div className="text-right">{formatNumber(value, 6, "round")}</div>;
        // return <div className="text-right"> {formatNumber(Math.ceil(value * 1000) / 1000, 2)}</div>;
      },
    },
    {
      title: t("Origin_Package_Service_Table.Table_Density", { ns: translations }),
      width: 150,
      // width: 85,
      dataIndex: "density_kg_cbm",
      key: "density_kg_cbm",
      render: (_, record) => {
        const value = record.density || 0;
        return <div className="text-right">{formatNumber(value, 6, "round")}</div>;
        // return <div className="text-right"> {formatNumber(Math.ceil(value * 1000) / 1000, 2)}</div>;
      },
    },
    {
      title: t("Origin_Package_Service_Table.Table_Rate", { ns: translations }),
      width: 150,
      // width: 50,
      dataIndex: "rate",
      key: "rate",
      render: (_, record) => {
        const originPackageTransportation = record?.app_originpackagetransportation;
        const rateOriginPackageData = isCompanyBill
          ? originPackageTransportation?.is_company_weight_formula
            ? originPackageTransportation?.apply_company_weight_formula
            : originPackageTransportation?.apply_company_volume_formula
          : originPackageTransportation?.is_customer_weight_formula
            ? originPackageTransportation?.apply_customer_weight_formula
            : originPackageTransportation?.apply_customer_volume_formula;

        // substring หลังเครื่องหมาย "="
        const rateString = String(rateOriginPackageData || "").split("=")[1] || "";
        // แปลงเป็น array ตัวเลข
        const rateValues = rateString.split("/").map((v) => Number(v.trim()));

        // mapping ตามลำดับ (A, B, C, D, E)
        const rateMap = {
          A: rateValues[0] || 0,
          B: rateValues[1] || 0,
          C: rateValues[2] || 0,
          D: rateValues[3] || 0,
          E: rateValues[4] || 0,
        };

        // ดึงตาม package_type (เช่น 'A', 'B', 'C', 'E')
        const pkgType = record?.app_originpackagetransportation?.app_originpackage?.package_type;
        const rateFromPackageType = rateMap[pkgType] ?? 0;

        const rateData = record?.rate === 0 ? rateFromPackageType : record?.rate;
        return (
          <InputNumber
            // style={{ width: 50 }}
            disabled={isLockedOriginPackageRow(record)}
            min={0}
            value={Number(rateData)}
            onChange={(value) => handleInputChange(value, record.id, "rate")}
          />
        );
      },
    },
    {
      title: t("Origin_Package_Service_Table.Table_Shipping_Cost", { ns: translations }),
      width: 150,
      // width: 50,
      dataIndex: "bill_amount",
      key: "bill_amount",
      render: (_, record) => {
        const shippingCost = record.bill_amount || 0;
        return <div className="text-right"> {formatNumber(Math.ceil(shippingCost * 1000) / 1000, 2)}</div>;
      },
    },
    {
      title: t("Origin_Package_Service_Table.Table_Other_Fee", { ns: translations }),
      width: 150,
      // width: 50,
      dataIndex: "package_service_ot",
      key: "package_service_ot",
      render: (_, record) => {
        const originPackage = record.app_originpackagetransportation?.app_originpackage ?? {};
        const originPackageService = originPackage.app_originpackageservice ?? [];
        const otPackage = originPackageService.find((item) => item.package_service === "OT");
        return (
          <InputNumber
            // style={{ width: 50 }}
            disabled={isLockedOriginPackageRow(record)}
            min={0}
            value={Number(otPackage?.service_cost ?? 0)}
            onChange={(value) => handleInputChange(value ?? 0, record.id, "ot")}
          />
        );
      },
    },
    {
      title: t("Origin_Package_Service_Table.Table_Total_Bill", { ns: translations }),
      width: 150,
      // width: 80,
      dataIndex: "total_bill",
      key: "total_bill",
      render: (_, record) => {
        const originPackage = record?.app_originpackagetransportation?.app_originpackage ?? {};
        const calShippingCost = record.bill_amount;
        const originPackageService = originPackage?.app_originpackageservice ?? [];
        const wcPackage = originPackageService.find((item) => item.package_service === "WC");
        const odPackage = originPackageService.find((item) => item.package_service === "OD");
        const inPackage = originPackageService.find((item) => item.package_service === "IN");
        const otPackage = originPackageService.find((item) => item.package_service === "OT");
        const destinationPackageService = record.app_destinationpackageservice;
        const ddPackage = Number(destinationPackageService?.service_cost || 0);
        const totalBill =
          Number(odPackage?.service_cost || 0) +
          Number(wcPackage?.service_cost || 0) +
          Number(inPackage?.service_cost || 0) +
          Number(otPackage?.service_cost || 0) +
          Number(ddPackage) +
          Number(calShippingCost);
        return <div className="text-right">{formatNumber(totalBill, 6, "round")}</div>;
        // return <div className="text-right"> {formatNumber(Math.ceil(totalBill * 1000) / 1000, 2)}</div>;
      },
    },
    {
      title: t("Origin_Package_Service_Table.Table_Note", { ns: translations }),
      width: 150,
      dataIndex: "note",
      key: "note",
      render: (_, record) => {
        return <p>{record.note || "-"}</p>; // Display the actual description
      },
    },
    {
      title: t("Origin_Package_Service_Table.Table_Created_Date", { ns: translations }),
      width: 150,
      dataIndex: "created_at",
      key: "created_at",
      render: (_, record) => {
        const created_at = record.created_at != null ? dayjs(record.created_at).format("YYYY-MM-DD hh:mm:ss A") : "-";
        return <p>{created_at}</p>;
      },
    },
    {
      title: t("Origin_Package_Service_Table.Table_Updated_Date", { ns: translations }),
      width: 150,
      dataIndex: "updated_at",
      key: "updated_at",
      render: (_, record) => {
        const updated_at = record.updated_at != null ? dayjs(record.updated_at).format("YYYY-MM-DD hh:mm:ss A") : "-";
        return <p>{updated_at}</p>; // Display the actual description
      },
    },
    // {
    //   title: t("Origin_Package_Service_Table.Table_ID", { ns: translations }),
    //   width: 150,
    //   dataIndex: "id",
    //   key: "id",
    //   render: (_, record) => {
    //     return <p>{record.id}</p>;
    //   },
    // },
    // {
    //   title: t("Origin_Package_Service_Table.Table_Description", { ns: translations }),
    //   width: 150,
    //   dataIndex: "description",
    //   key: "description",
    //   render: (_, record) => {
    //     return <p>{record.description}</p>;
    //   },
    // },
    {
      title: t("Origin_Package_Service_Table.Table_Updated_By", { ns: translations }),
      width: 150,
      dataIndex: "updated_by",
      key: "updated_by",
      render: (_, record) => {
        return <p>{record.updated_by || "-"}</p>; // Display the actual description
      },
    },
  ];

  const fetchCompanyBillItem = useCallback(
    async (param) => {
      const params = {
        ...param,
        company_bill_no: companyBillItemId ?? dataCompanyBill.id,
      };

      try {
        const data = await companyBillService.getCompanyBillItem(accessToken, params);
        // ✅ Add default fields OD, WD, IN if not exist
        const mappedItems = (data?.query ?? []).map((item) => {
          const total_od = getServiceCostFromRow(item, "OD");
          const total_wc = getServiceCostFromRow(item, "WC");
          const total_in = getServiceCostFromRow(item, "IN");
          const total_ot = getServiceCostFromRow(item, "OT");
          return {
            ...item,
            total_od,
            total_wc,
            total_in,
            total_ot,
          };
        });

        dataCompanyBill.app_companybillitem = data?.query ?? [];
        let sum_total_bill = 0.0;
        data?.query?.map((value) => {
          const originService = value.app_originpackagetransportation?.app_originpackage?.app_originpackageservice;
          if (originService) {
            sum_total_bill = originService.reduce((sum, item) => sum + Number(item.service_cost), 0);
            dataCompanyBill.app_companybillitem.map((item) => {
              if (item.id === value.id) {
                item.rate = value.rate;
                item.bill_amount = value.bill_amount;
              }
            });
          }
        });

        const package_total_bill_amount = data?.query?.reduce((sum, item) => sum + Number(item.bill_amount), 0);
        dataCompanyBill.package_total_bill_amount = package_total_bill_amount;
        dataCompanyBill.sum_total_bill = Number(sum_total_bill) + Number(package_total_bill_amount);
        setCompanyBillItem(mappedItems);
        setTotal(data?.totalCount ?? 0);
        setSumTotal(data?.sumTotal ?? 0);
        setSearch({ ...params });
        onItemsChange({
          items: mappedItems,
          sumTotal: dataCompanyBill.package_total_bill_amount,
          packageTotal: package_total_bill_amount,
          totalPrice: dataCompanyBill.sum_total_bill,
        });
      } catch (err) {
        console.error("[CreateOriginPackageTransportationService] fetchCompanyBillItem: ", err);
        const { message } = settings.getErrorMessage(err);
        openNotificationWithIcon(settings.NotificationType.ERROR, <>{"ERROR."}</>, <>{`${message}`}</>);
      } finally {
        setLoading(false);
      }
    },
    [accessToken, companyBillItemId, dataCompanyBill?.id, onItemsChange]
  );

  const fetchCustomerBillItem = useCallback(
    async (param) => {
      const params = {
        ...param,
        customer_bill_no: companyBillItemId ?? dataCompanyBill.id,
      };
      try {
        const data = await customerBillService.getCustomerBillItem(accessToken, params);
        // ✅ Add default fields OD, WD, IN if not exist
        const mappedItems = (data?.query ?? []).map((item) => {
          const total_od = getServiceCostFromRow(item, "OD");
          const total_wc = getServiceCostFromRow(item, "WC");
          const total_in = getServiceCostFromRow(item, "IN");
          const total_ot = getServiceCostFromRow(item, "OT");
          return {
            ...item,
            total_od,
            total_wc,
            total_in,
            total_ot,
          };
        });

        dataCompanyBill.app_customerbillitem = data?.query ?? [];
        let sum_total_bill = 0.0;
        data?.query?.map((value) => {
          const originService = value.app_originpackagetransportation?.app_originpackage?.app_originpackageservice;
          if (originService) {
            sum_total_bill = originService.reduce((sum, item) => sum + Number(item.service_cost || 0), 0);
            dataCompanyBill.app_customerbillitem.map((item) => {
              if (item.id === value.id) {
                item.rate = value.rate;
                item.bill_amount = value.bill_amount;
              }
            });
          }
        });
        const package_total_bill_amount = data?.query?.reduce((sum, item) => sum + Number(item.bill_amount), 0);
        dataCompanyBill.package_total_bill_amount = package_total_bill_amount;
        dataCompanyBill.sum_total_bill = Number(sum_total_bill) + Number(package_total_bill_amount);
        dataCompanyBill.total_price = dataCompanyBill.sum_total_bill;
        setCompanyBillItem(mappedItems);
        setTotal(data?.totalCount ?? 0);
        setSumTotal(data?.sumTotal ?? 0);
        setSearch({ ...param });
        onItemsChange({
          items: mappedItems,
          sumTotal: dataCompanyBill.package_total_bill_amount,
          packageTotal: package_total_bill_amount,
          totalPrice: dataCompanyBill.sum_total_bill,
        });
      } catch (err) {
        console.error("[CreateOriginPackageTransportationService] fetchCustomerBillItem: ", err);
        const { message } = settings.getErrorMessage(err);
        openNotificationWithIcon(settings.NotificationType.ERROR, <>{"ERROR."}</>, <>{`${message}`}</>);
      } finally {
        setLoading(false);
      }
    },
    [accessToken]
  );

  useEffect(() => {
    if (!accessToken || hasFetchedRef.current) return;
    if (isCompanyBill) {
      fetchCompanyBillItem(initPage);
    } else {
      fetchCustomerBillItem(initPage);
    }
    hasFetchedRef.current = true;
  }, [accessToken, isCompanyBill]);

  return (
    <>
      {contextHolder}
      <Spin spinning={loading}>
        <Row align="middle" justify="end">
          <Col span={24}>
            <Table
              columns={columns}
              dataSource={Array.isArray(companyBillItem) ? companyBillItem : []}
              scroll={{ x: "max-content" }}
              rowKey={(record) => record.id}
              style={{ cursor: "pointer" }}
              // style={{ cursor: "pointer", tableLayout: "fixed", wordBreak: "break-all" }}
              pagination={{
                current: search.page,
                pageSize: search.limit,
                total: total,
                onChange: (page, limit) => {
                  if (isCompanyBill) {
                    fetchCompanyBillItem({ ...search, page, limit });
                  } else {
                    fetchCustomerBillItem({ ...search, page, limit });
                  }
                },
                showSizeChanger: true,
              }}
              footer={() => (
                <Row justify="end" style={{ marginTop: 8 }}>
                  <Col>
                    <b>
                      Total:{" "}
                      {sumTotal.toLocaleString(undefined, {
                        minimumFractionDigits: 4,
                      })}
                    </b>
                  </Col>
                </Row>
              )}
            />
            {hasPermission(isCompanyBill ? permissions.CompanyBillItem.ADD : permissions.CustomerBillItem.ADD) && (
              <Row justify="center" style={{ marginTop: 16 }}>
                <Button type="primary" onClick={handleSave}>
                  Save Services
                </Button>
              </Row>
            )}
          </Col>
          <ModalDestinationService
            isCompanyBill={isCompanyBill}
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            handleOk={handleOk}
            handleCancel={handleCancel}
            fetchCompanyBillItem={fetchCompanyBillItem}
            fetchCustomerBillItem={fetchCustomerBillItem}
            item={item}
            setItem={setItem}
            isEdit={isEdit}
            companyBillItem={companyBillItem[0]}
          />
        </Row>
      </Spin>
    </>
  );
};
